import { useEffect, useMemo } from 'react';

import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';

const DEFAULT_MOBILE_THRESHOLD = 1;
const DEFAULT_DESKTOP_THRESHOLD = 0.5;
const DEFAULT_SAFETY_SCREENS = 0.5;
const DEFAULT_MIN_PAGE_SIZE = 20;
const DEFAULT_MAX_PAGE_SIZE = 48;
const SUPPORTED_GRID_COLUMNS = [1, 2, 3, 4, 6];

export interface IFlashListEstimatedRowsOnScreenByBreakpoint {
  mobile: number;
  desktop: number;
}

export interface IFlashListMaxColsOnScreenByBreakpoint {
  mobile?: number;
  desktop?: number;
}

interface IUseFlashListOptimizerOptions {
  estimatedRowsOnScreen: IFlashListEstimatedRowsOnScreenByBreakpoint;
  maxColsOnScreen?: IFlashListMaxColsOnScreenByBreakpoint;
  thresholdMobile?: number;
  thresholdDesktop?: number;
  safetyScreens?: number;
  minPageSize?: number;
  maxPageSize?: number;
}

function greatestCommonDivisor(a: number, b: number): number {
  return b === 0 ? a : greatestCommonDivisor(b, a % b);
}

function leastCommonMultiple(a: number, b: number): number {
  return (a * b) / greatestCommonDivisor(a, b);
}

function roundUpToMultiple(value: number, multiple: number): number {
  return Math.ceil(value / multiple) * multiple;
}

function roundDownToMultiple(value: number, multiple: number): number {
  return Math.floor(value / multiple) * multiple;
}

function getPageSizeMultiple(maxColumns: number): number {
  return SUPPORTED_GRID_COLUMNS.filter((columns) => columns <= maxColumns).reduce(
    (multiple, columns) => leastCommonMultiple(multiple, columns),
    1
  );
}

export function useFlashListOptimizer({
  estimatedRowsOnScreen,
  maxColsOnScreen,
  thresholdMobile = DEFAULT_MOBILE_THRESHOLD,
  thresholdDesktop = DEFAULT_DESKTOP_THRESHOLD,
  safetyScreens = DEFAULT_SAFETY_SCREENS,
  minPageSize = DEFAULT_MIN_PAGE_SIZE,
  maxPageSize = DEFAULT_MAX_PAGE_SIZE,
}: IUseFlashListOptimizerOptions) {
  const isLargeScreen = useIsLargeScreen();

  const threshold = isLargeScreen ? thresholdDesktop : thresholdMobile;
  const resolvedMaxCols = {
    mobile: maxColsOnScreen?.mobile ?? 1,
    desktop: maxColsOnScreen?.desktop ?? 1,
  };
  const estimatedRows = isLargeScreen
    ? estimatedRowsOnScreen.desktop
    : estimatedRowsOnScreen.mobile;
  const maxColumns = Math.max(
    1,
    Math.floor(isLargeScreen ? resolvedMaxCols.desktop : resolvedMaxCols.mobile)
  );

  const { itemsOnScreen, pageSize } = useMemo(() => {
    const pageSizeMultiple = getPageSizeMultiple(maxColumns);
    const estimatedItemsOnScreen = Math.ceil(estimatedRows * maxColumns);
    const minimumItems = estimatedItemsOnScreen * (1 + threshold + safetyScreens);
    const minAlignedPageSize = roundUpToMultiple(minPageSize, pageSizeMultiple);
    const calculatedPageSize = roundUpToMultiple(minimumItems, pageSizeMultiple);
    const maxAlignedPageSize = roundDownToMultiple(maxPageSize, pageSizeMultiple);

    return {
      itemsOnScreen: estimatedItemsOnScreen,
      pageSize: Math.min(Math.max(calculatedPageSize, minAlignedPageSize), maxAlignedPageSize),
    };
  }, [estimatedRows, maxColumns, maxPageSize, minPageSize, safetyScreens, threshold]);

  const output = useMemo(
    () => ({
      itemsOnScreen,
      onEndReachedThreshold: threshold,
      pageSize,
    }),
    [itemsOnScreen, pageSize, threshold]
  );

  useEffect(() => {
    if (!__DEV__) return;
    console.log('[useFlashListOptimizer]', output, {
      estimatedRows,
      maxColumns,
    });
  }, [
    estimatedRowsOnScreen.desktop,
    estimatedRowsOnScreen.mobile,
    estimatedRows,
    maxColsOnScreen?.desktop,
    maxColsOnScreen?.mobile,
    maxColumns,
    output,
    resolvedMaxCols.desktop,
    resolvedMaxCols.mobile,
  ]);

  return output;
}
